import React, { useCallback, useMemo } from 'react';
import { useT, formatCurrencyUnit } from '@core/i18n';
import { useActiveGameStore } from '@core/gameengine/state/activeGameStore';
import { buildGamePublicAssetUrl } from '@core/utils/assetFolderSlug';
import { SKYX_NEW_CHAT_WIN_CARD_THEME } from '@core/lobby/skyx/skyxChatWinCardTheme';
import { useSkyxPlayerAvatarUrl } from '@core/hooks/useSkyxPlayerAvatarUrl';
import {
  formatWinShareNumber,
  type SkyxWinShareData,
} from '@core/utils/skyxChatWinShare';
import { formatSkyxPlayerDisplayName } from '@core/utils/skyxPlayerDisplayName';

interface SkyxNewChatWinCardProps {
  data: SkyxWinShareData;
  onProvablyFairOpen?: () => void;
  date?: string;
}

const theme = SKYX_NEW_CHAT_WIN_CARD_THEME;

export function formatShareDate(date: Date): string {
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear().toString().slice(-2);
  return `${day}.${month}.${year}`;
}

export const SkyxNewChatWinCard: React.FC<SkyxNewChatWinCardProps> = ({
  data,
  onProvablyFairOpen,
  date,
}) => {
  const t = useT('skyx_new');
  const openProvablyFairModal = useActiveGameStore(
    state => state.openProvablyFairModal
  );
  const playerId = data.playerId ?? data.player;
  const avatarUrl = useSkyxPlayerAvatarUrl(playerId, data.avatarUrl);
  const displayName = formatSkyxPlayerDisplayName(data.player);
  const shareDate = useMemo(() => date ?? formatShareDate(new Date()), [date]);

  const shieldUrl = buildGamePublicAssetUrl(
    'skyx_new',
    'images/ui/shield-icon.png'
  );

  const roundIdNumber = Number(data.roundId);
  const canOpenProvablyFair =
    !!data.roundId &&
    !Number.isNaN(roundIdNumber) &&
    roundIdNumber > 0 &&
    typeof openProvablyFairModal === 'function';

  const handleShieldClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      event.preventDefault();
      if (!canOpenProvablyFair || !openProvablyFairModal) return;

      openProvablyFairModal({
        roundId: roundIdNumber,
        bust: data.round,
        serverSeed: '',
        serverSeedHash: '',
        timeStamp: 0,
      });

      // Keep chat (parent) open while the PF / seed child modal is shown.
      onProvablyFairOpen?.();
    },
    [
      canOpenProvablyFair,
      data.round,
      onProvablyFairOpen,
      openProvablyFairModal,
      roundIdNumber,
    ]
  );

  const betLabel = t('ui.control_panel.bet_panel.bet_currency', {
    currency: formatCurrencyUnit(data.currency),
  });
  const winLabel = t('ui.hud.win_currency', {
    currency: formatCurrencyUnit(data.currency),
  });
  const resultLabel = t('ui.scoreboard.result');
  const roundMaxLabel = t('ui.scoreboard.round_max');

  return (
    <div style={styles.card}>
      <div style={styles.topPlayerInfo}>
        <div style={styles.topPlayerDetails}>
          {avatarUrl ? (
            <img src={avatarUrl} alt="" style={styles.avatar} />
          ) : (
            <div style={styles.avatarPlaceholder} aria-hidden>
              {displayName.charAt(0).toUpperCase()}
            </div>
          )}
          <div>
            <div style={styles.playerName}>{displayName}</div>
            <div style={styles.playerDate}>{shareDate}</div>
          </div>
        </div>

        <button
          type="button"
          className="autoplay-3d-btn"
          style={{
            ...styles.shieldButton,
            cursor: canOpenProvablyFair ? 'pointer' : 'default',
            opacity: canOpenProvablyFair ? 1 : 0.45,
          }}
          onClick={handleShieldClick}
          disabled={!canOpenProvablyFair}
          aria-label={t('ui.popuppanel.provably_fair.icon_alt')}
          data-skyx-chat-win-shield
        >
          <img
            src={shieldUrl}
            alt=""
            style={{
              width: `${theme.shieldIconSizePx}px`,
              height: `${theme.shieldIconSizePx}px`,
              filter: 'brightness(0) invert(1)',
            }}
          />
        </button>
      </div>

      <div style={styles.topStatsContainer}>
        <div style={styles.statCell}>
          <span style={styles.statLabel}>{betLabel}</span>
          <span style={{ ...styles.statValue, minWidth: '7ch' }}>
            {formatWinShareNumber(data.bet)}
          </span>
        </div>
        <div style={styles.statCell}>
          <span style={styles.statLabel}>{resultLabel}</span>
          <span style={{ ...styles.cashoutValue, minWidth: '9ch' }}>
            {formatWinShareNumber(data.cashout)}x
          </span>
        </div>
        <div style={styles.statCell}>
          <span style={styles.statLabel}>{winLabel}</span>
          <span style={{ ...styles.statValue, minWidth: '9ch' }}>
            {formatWinShareNumber(data.win)}
          </span>
        </div>
        <div style={styles.statCell}>
          <span style={styles.statLabel}>{roundMaxLabel}</span>
          <span style={{ ...styles.cashoutValue, minWidth: '8ch' }}>
            {formatWinShareNumber(data.round)}x
          </span>
        </div>
      </div>

      {data.caption ? <p style={styles.caption}>{data.caption}</p> : null}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  card: {
    width: '100%',
    maxWidth: '100%',
    background: theme.cardBg,
    border: `1px solid ${theme.cardBorder}`,
    borderRadius: theme.cardRadius,
    padding: '10px 12px',
    boxSizing: 'border-box',
    fontFamily: "'Roboto', sans-serif",
  },
  topPlayerInfo: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  topPlayerDetails: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    minWidth: 0,
  },
  avatar: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    objectFit: 'cover',
    flexShrink: 0,
  },
  avatarPlaceholder: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#271574',
    color: '#ffffff',
    fontSize: '13px',
    fontWeight: 600,
  },
  playerName: {
    color: theme.playerNameColor,
    fontSize: '13px',
    fontWeight: 600,
    lineHeight: 1.3,
    overflow: 'visible',
    textOverflow: 'clip',
    whiteSpace: 'nowrap',
  },
  playerDate: {
    color: theme.dateColor,
    fontSize: '11px',
    lineHeight: 1.3,
    marginTop: '2px',
  },
  shieldButton: {
    width: `${theme.shieldButtonSizePx}px`,
    height: `${theme.shieldButtonSizePx}px`,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    borderRadius: '50%',
    background: theme.shieldGradient,
    border: 'none',
    boxShadow: theme.shieldButtonShadow,
  },
  topStatsContainer: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
    columnGap: '10px',
    rowGap: '10px',
    marginTop: '10px',
    minWidth: 0,
  },
  statCell: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '2px',
    minWidth: 0,
  },
  statLabel: {
    color: theme.labelColor,
    fontSize: '11px',
    lineHeight: 1.25,
    whiteSpace: 'normal',
    wordBreak: 'break-word',
  },
  statValue: {
    color: theme.valueColor,
    fontSize: '13px',
    fontWeight: 600,
    lineHeight: 1.3,
    whiteSpace: 'nowrap',
    fontVariantNumeric: 'tabular-nums',
  },
  cashoutValue: {
    color: theme.cashoutColor,
    fontSize: '13px',
    fontWeight: 600,
    lineHeight: 1.3,
    whiteSpace: 'nowrap',
    fontVariantNumeric: 'tabular-nums',
  },
  caption: {
    margin: '10px 0 0',
    paddingTop: '10px',
    borderTop: `1px solid ${theme.cardBorder}`,
    color: theme.valueColor,
    fontSize: '13px',
    lineHeight: 1.45,
    wordBreak: 'break-word',
    whiteSpace: 'pre-wrap',
  },
};
